import { type TopPlayer } from "@/api/tournamentAnalysis";
import { Loader } from "../../common/loader";
import { useNavigate } from "@tanstack/react-router";
import { getPointsBadge } from "@/lib/utils";
import { ExternalLink } from "lucide-react";

interface TournamentAnalysisTopPerformersCardProps {
    topPlayers?: TopPlayer[];
    isLoading?: boolean;
}

export function TournamentAnalysisTopPerformersCard({
    topPlayers,
    isLoading,
}: TournamentAnalysisTopPerformersCardProps) {
    const navigate = useNavigate();
    const displayedPlayers = topPlayers?.filter(
        (player, index, self) => self.findIndex((p) => p.playerId === player.playerId) === index
    );

    const getRankColor = (rank: number): string => {
        if (rank === 1) return "var(--color-medal-gold)";
        if (rank === 2) return "var(--color-rank-silver)";
        if (rank === 3) return "var(--color-rank-bronze)";
        return "var(--color-on-surface-variant)";
    };

    if (isLoading) {
        return (
            <div
                className="bg-surface-container-lowest rounded-xl flex items-center justify-center shadow-sm"
                style={{ minHeight: 200 }}
            >
                <Loader message="Loading top performers..." />
            </div>
        );
    }

    if (!topPlayers || !displayedPlayers || displayedPlayers.length === 0) {
        return (
            <div
                className="bg-surface-container-lowest rounded-xl flex items-center justify-center shadow-sm"
                style={{ minHeight: 200 }}
            >
                <p className="text-on-surface-variant">No player data available</p>
            </div>
        );
    }

    const headerCells = [
        { label: "#", className: "w-10" },
        { label: "Player", className: "" },
        { label: "Matches", className: "w-20" },
        { label: "KPI", className: "w-16" },
    ];

    return (
        <div
            id="onboarding-tournament-top-performers"
            className="bg-surface-container-lowest rounded-xl shadow-sm overflow-hidden h-full flex flex-col"
        >
            <div className="md:hidden flex flex-col">
                {displayedPlayers.map((player, index) => {
                    const rank = index + 1;
                    const score = Math.round(player.kpiScore);
                    const { bg, text, border } = getPointsBadge(score);
                    const rowBg = index % 2 === 0 ? "bg-surface-container-lowest" : "bg-surface-container-low";
                    return (
                        <div
                            key={player.playerId}
                            className={`flex items-center gap-3 px-4 py-3.5 border-b border-outline-variant/10 cursor-pointer ${rowBg}`}
                            onClick={() => navigate({ to: `/player/${player.playerId}` })}
                        >
                            <span
                                className={`font-headline font-bold text-sm w-9 text-center shrink-0 tabular-nums ${
                                    rank === 1 ? "text-[#ba0a06]" : "text-on-surface-variant"
                                }`}
                            >
                                #{rank.toString().padStart(2, "0")}
                            </span>
                            <div className="flex flex-col flex-1 min-w-0">
                                <div className="flex items-center gap-1 group">
                                    <span className="font-bold text-sm text-primary truncate">{player.name}</span>
                                    <ExternalLink className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-primary" />
                                </div>
                                <span className="text-xs text-on-surface-variant">{player.matchesPlayed} matches</span>
                            </div>
                            <div
                                className={`inline-flex items-center px-2 py-0.5 ${bg} ${text} rounded font-bold text-sm border ${border} shrink-0`}
                            >
                                {score}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="hidden md:block min-h-0 overflow-y-auto overflow-x-hidden max-h-[484px]">
                <table className="w-full border-collapse table-fixed">
                    <thead>
                        <tr className="border-0">
                            {headerCells.map(({ label, className }) => (
                                <th
                                    key={label}
                                    className={`data-table-header px-4 py-3.5 font-headline font-black text-[10px] uppercase tracking-widest text-left whitespace-nowrap ${className}`}
                                    style={{
                                        position: "sticky",
                                        top: 0,
                                        zIndex: 10,
                                    }}
                                >
                                    {label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {displayedPlayers.map((player, index) => {
                            const rank = index + 1;
                            return (
                                <tr
                                    key={player.playerId}
                                    className="data-table-row cursor-pointer transition-colors border-0 odd:bg-surface-container-lowest even:bg-surface-container-low"
                                    onClick={() => navigate({ to: `/player/${player.playerId}` })}
                                >
                                    <td
                                        className="px-4 py-3 text-sm font-bold whitespace-nowrap"
                                        style={{ color: getRankColor(rank) }}
                                    >
                                        {rank}
                                    </td>
                                    <td className="px-4 py-3 text-sm font-bold text-primary max-w-0">
                                        <div className="flex items-center gap-1.5 group min-w-0">
                                            <span className="hover:underline truncate">{player.name}</span>
                                            <ExternalLink className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-primary" />
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-on-surface-variant whitespace-nowrap">
                                        {player.matchesPlayed}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        {(() => {
                                            const score = Math.round(player.kpiScore);
                                            const { bg, text, border } = getPointsBadge(score);
                                            return (
                                                <div
                                                    className={`inline-flex items-center px-2 py-0.5 ${bg} ${text} rounded font-bold text-sm border ${border}`}
                                                >
                                                    {score}
                                                </div>
                                            );
                                        })()}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
